import * as yup from 'yup'
import { defineMessages, IntlShape } from 'react-intl'

import { sanitizePrice } from 'utils/math'
import { isEmpty, round } from 'lodash'
import { TPaidPayment, ISelectedPayment } from './types'
import { IBookingModel } from 'features/Bookings/types'
import { GTBookingV2PaymentProcessor } from 'gql/types.generated'

const amountToRefundMessages = defineMessages({
  required: {
    id: 'booking.cancellation.amountToRefund.required',
    defaultMessage: 'Required'
  },
  isLtePaidAmount: {
    id: 'booking.cancellation.amountToRefund.isLtePaidAmount',
    defaultMessage: 'The amount to refund can not be greater than the payment amount.'
  },
  isLteKreditsPaidAmount: {
    id: 'booking.cancellation.amountToRefund.isLteKreditsPaidAmount',
    defaultMessage: 'The amount to refund can not be greater than the total payments.'
  }
})

const selectedTicketsMessages = defineMessages({
  required: {
    id: 'booking.cancellation.selectedTickets.required',
    defaultMessage: 'You need to select at least one ticket'
  }
})

const selectedPaymentsMessages = defineMessages({
  required: {
    id: 'booking.cancellation.selectedPayments.required',
    defaultMessage: 'You need to select at least one payment'
  }
})

const reasonMessages = defineMessages({
  required: {
    id: 'booking.cancellation.reason.required',
    defaultMessage: 'The reason is required'
  }
})

export const amountsAreMatching = ({
  selectedTicketsIds,
  selectedPayments,
  booking
}: {
  selectedTicketsIds: string[]
  selectedPayments: ISelectedPayment[]
  booking: IBookingModel
}): boolean => {
  if (isEmpty(selectedTicketsIds) || isEmpty(selectedPayments)) {
    return true
  }
  const selectedTickets = booking.tickets.filter(({ id }): TSFixMe => selectedTicketsIds.includes(id))
  const totalPaidAmountByTickets = selectedTickets.reduce((total, selectedTicket): TSFixMe => {
    return total + selectedTicket.paidAmount
  }, 0)
  const totalAmountToRefund = selectedPayments.reduce((total: number, selectedPayment: ISelectedPayment): TSFixMe => {
    return total + (selectedPayment.amountToRefund * 100 || 0)
  }, 0)
  return sanitizePrice(totalAmountToRefund) === totalPaidAmountByTickets
}

export const schema = (
  intl: IntlShape,
  booking: IBookingModel,
  paidPayments: TPaidPayment[],
  isNewBookingCancellationFeatureFlagEnabled: boolean
): yup.AnyObjectSchema => {
  const baseShape = {
    reason: yup.string().required(intl.formatMessage(reasonMessages.required)),
    selectedTicketsIds: yup.array().min(1, intl.formatMessage(selectedTicketsMessages.required))
  }
  const hasPaidPayments = !isEmpty(paidPayments)
  if (hasPaidPayments) {
    return yup.object().shape({
      ...baseShape,
      selectedPayments: yup
        .array()
        .of(
          yup.object().shape({
            paymentId: yup.string(),
            paidAmount: yup.number(),
            amountToRefund: yup
              .number()
              .required(intl.formatMessage(amountToRefundMessages.required))
              .test('is-lte-paidAmount', intl.formatMessage(amountToRefundMessages.isLtePaidAmount), function () {
                if (isNewBookingCancellationFeatureFlagEnabled) {
                  return true
                }
                const paymentProcessor = this.parent.processor
                if (paymentProcessor === GTBookingV2PaymentProcessor.Kredit) {
                  return true
                }
                const { paidAmount, amountToRefund } = this.parent
                return amountToRefund <= round(paidAmount / 100, 2)
              })
              .test(
                'is-lte-kredits-paidAmount',
                intl.formatMessage(amountToRefundMessages.isLteKreditsPaidAmount),
                function () {
                  if (isNewBookingCancellationFeatureFlagEnabled) {
                    return true
                  }
                  const paymentProcessor = this.parent.processor

                  if (paymentProcessor !== GTBookingV2PaymentProcessor.Kredit) {
                    return true
                  }
                  const { amountToRefund } = this.parent
                  const totalAmountToRefund = paidPayments.reduce((total, payment) => {
                    return total + payment.amount
                  }, 0)

                  return amountToRefund <= round(totalAmountToRefund / 100, 2)
                }
              )
          })
        )
        .test('payments-is-not-empty', intl.formatMessage(selectedPaymentsMessages.required), function () {
          if (isNewBookingCancellationFeatureFlagEnabled) {
            return true
          }
          const { selectedTicketsIds, selectedPayments } = this.parent
          if (isEmpty(selectedTicketsIds)) {
            return true
          }
          const selectedTickets = booking.tickets.filter(({ id }) => selectedTicketsIds.includes(id))
          const paidTicket = selectedTickets.find(({ paidAmount }): boolean => paidAmount > 0)
          if (isEmpty(paidTicket)) {
            return true
          }

          return !isEmpty(selectedPayments)
        })
    })
  } else {
    return yup.object().shape({
      ...baseShape
    })
  }
}
